import moment from 'moment';
import { Col, Row, Space, Tooltip } from 'antd';
import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import SearchBar from 'components/new-ui/base/searchbar/SearchBar';
import JobsTable from 'views/jobs/components/JobsTable';
import AssignJobModal from 'views/manifests/components/AssignJobModal';
import ArchiveJobModal from 'views/manifests/components/ArchiveJobModal';
import useManifests from 'hooks/useManifests';
import { GetJobsParams, JobResponse, JobStatuses } from 'store/jobs/job';
import Notification from 'components/ui/base/notification/Notification';
import useGlobalOverlay from 'hooks/useGlobalOverlay';
import useSession from 'hooks/useSession';
import { deleteThirdPartyJob, listThirdPartyJobs, removeJobFromManifest } from 'store/manifests/client';
import RemoveJobFromManifestModal from 'views/manifests/components/RemoveJobFromManifestModal';
import { archiveJob, listJobsWithEmailNotificationStatuses, unarchiveJob } from 'store/jobs/client';
import { hasPermissions } from 'config/roles/hasPermissions';
import { Events } from 'utils/events.enum';
import JobFiltersModal from 'views/jobs/components/JobFiltersModal';
import { useJobsFilters } from 'views/jobs/useJobsFiters';
import Button from 'components/new-ui/base/button/Button';
import AppliedJobFilters from 'views/jobs/components/AppliedJobFilters';
import DatePicker from 'components/new-ui/base/date-picker/DatePicker';
import ArchiveFilter from 'components/new-ui/composed/archive-filter/ArchiveFilter';
import ManifestOverviewHeader from '../components/ManifestOverviewHeader';
import UnarchiveJobModal from 'views/manifests/components/UnarchiveJobModal';
import useIndividuals from 'hooks/useIndividuals';
import useCustomers from 'hooks/useCustomers';
import ThirdPartyJobsTable from 'views/manifests/components/third-party-job/ThirdPartyJobsTable';
import AddThirdPartyJobModal from 'views/manifests/components/third-party-job/AddThirdPartyJobModal';
import DeleteThirdPartyJobModal from 'views/manifests/components/third-party-job/DeleteThirdPartyJobModal';
import { H2 } from 'components/new-ui/base/typography/styles';
import ThirdPartyJobIcon from 'views/manifests/components/third-party-job/ThirdPartyJobIcon';
import { ThirdPartyJobResponse } from 'store/manifests/types';
import { TableRowSelection } from 'antd/lib/table/interface';
import EmailJobModal from '../components/email-job/EmailJobModal';
import EditJobModal from 'views/jobs/components/edit-job/EditJobModal';

type ModalState =
    | { type: null }
    | { type: 'assign'; jobId: string }
    | { type: 'archive'; jobId: string }
    | { type: 'unarchive'; jobId: string }
    | { type: 'remove'; jobId: string; manifestId: string }
    | { type: 'edit' }
    | { type: 'email-job'; selectedJobIds: React.Key[] };

const ManifestOverview = () => {
    const { manifest } = useManifests();
    const navigate = useNavigate();
    const { showGlobalOverlay, hideGlobalOverlay } = useGlobalOverlay();
    const { userInfo } = useSession();
    const { listIndividuals } = useIndividuals();
    const { listCustomers } = useCustomers();

    const manifestJobsRequest = useCallback(
        (params: GetJobsParams) => listJobsWithEmailNotificationStatuses(manifest!.id, params),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [manifest?.id]
    );

    const {
        data,
        isLoading,
        refetch,
        setArchived,
        archived,
        queryInputVal,
        setQueryInputVal,
        setPage,
        setExactDate,
        exactDate,
        tags,
        clearFilter,
        clearAll,
        pagination,
        sortDirection,
        sort,
        filters,
        handleFiltersChange,
        handleTableChange,
    } = useJobsFilters(
        (params) => ({ ...params, manifestIds: manifest?.id ? [manifest.id] : undefined }),
        manifest?.id ? manifestJobsRequest : undefined
    );

    const [selectedJob, setSelectedJob] = useState<JobResponse | null>(null);
    const [modal, setModal] = useState<ModalState>({ type: null });
    const [openFiltersModal, setOpenFiltersModal] = useState(false);
    const [thirdPartyJobs, setThirdPartJobs] = useState<ThirdPartyJobResponse[]>([]);
    const [loadingThirdPartyJobs, setLoadingThirdPartyJobs] = useState(false);
    const [selectedThirdPartyJob, setSelectedThirdPartyJob] = useState<ThirdPartyJobResponse | null>(null);
    const [thirdPartyModal, setThirdPartyModal] = useState<'edit' | 'delete' | null>(null);
    const [selectedJobIds, setSelectedJobIds] = useState<React.Key[]>([]);

    const openAssignJobModal = (jobId: string) => setModal({ type: 'assign', jobId });
    const openArchiveJobModal = (jobId: string) => setModal({ type: 'archive', jobId });
    const openUnarchiveJobModal = (jobId: string) => setModal({ type: 'unarchive', jobId });
    const openEditModal = () => setModal({ type: 'edit' });
    const openRemoveJobFromManifestModal = (jobId: string, manifestId: string) =>
        setModal({ type: 'remove', jobId, manifestId });
    const closeModal = () => setModal({ type: null });

    const isAssignOpen = modal.type === 'assign';
    const isArchiveOpen = modal.type === 'archive';
    const isRemoveOpen = modal.type === 'remove';
    const isEditModalOpen = modal.type === 'edit';
    const currentJobId = 'jobId' in modal ? modal.jobId : null;
    const currentManifestId = modal.type === 'remove' ? modal.manifestId : null;
    const canAssignManifests = hasPermissions(userInfo, 'manifests', 'UPDATE');

    const rowSelection: TableRowSelection<JobResponse> = {
        type: 'checkbox',
        selectedRowKeys: selectedJobIds,
        onChange: (keys, rows) => {
            setSelectedJobIds(keys);
        },
        getCheckboxProps: (record) => ({
            disabled: record.status !== JobStatuses.COMPLETED,
        }),
        renderCell: (_checked, record, _index, originNode) => {
            if (record.status === JobStatuses.COMPLETED) return originNode;
            return (
                <Tooltip title='Only jobs with status "Cleared" can be marked as ready to be send.'>
                    {originNode}
                </Tooltip>
            );
        },
    };

    const handleArchiveJob = async () => {
        if (!manifest?.id) {
            Notification({
                type: 'error',
                placement: 'top',
                messageTitle: 'Error',
                description: 'No Manifest selected',
            });
            return;
        }

        if (!selectedJob?.id) {
            Notification({
                type: 'error',
                placement: 'top',
                messageTitle: 'Error',
                description: 'No job selected',
            });
            return;
        }
        try {
            showGlobalOverlay({ type: 'LoadingOverlay' });
            await archiveJob(selectedJob.id);
            Notification({
                type: 'success',
                placement: 'top',
                messageTitle: 'Job Archived',
                description: 'Successfully archived job',
            });
            await refetch();
        } catch (error) {
            Notification({
                type: 'error',
                placement: 'top',
                messageTitle: 'Error',
                description: 'Could not archive job',
            });
        } finally {
            hideGlobalOverlay();
            closeModal();
        }
    };

    const handleUnarchiveJob = async () => {
        if (!selectedJob?.id) {
            Notification({
                type: 'error',
                placement: 'top',
                messageTitle: 'Error',
                description: 'No job selected',
            });
            return;
        }
        try {
            showGlobalOverlay({ type: 'LoadingOverlay' });
            await unarchiveJob(selectedJob?.id);
            Notification({
                type: 'success',
                placement: 'top',
                messageTitle: 'Job Unarchived',
                description: 'Successfully Unarchived job',
            });
            await refetch();
        } catch (error) {
            Notification({
                type: 'error',
                placement: 'top',
                messageTitle: 'Error',
                description: 'Could not unarchive job',
            });
        } finally {
            hideGlobalOverlay();
            closeModal();
        }
    };

    const handleRemoveJobFroManifest = async () => {
        if (!currentJobId || !currentManifestId) {
            Notification({
                type: 'error',
                placement: 'top',
                messageTitle: 'Error',
                description: 'Missing job or manifest id',
            });
            return;
        }

        try {
            showGlobalOverlay({
                type: 'LoadingOverlay',
                payload: {
                    message: 'Removing job from manifest',
                },
            });

            await removeJobFromManifest({ jobId: currentJobId, manifestId: currentManifestId });

            closeModal();

            await refetch();

            Notification({
                type: 'success',
                placement: 'top',
                messageTitle: 'Success',
                description: 'Successfully removed job from manifest',
            });
        } catch (error) {
            Notification({
                type: 'error',
                placement: 'top',
                messageTitle: 'Error',
                description: 'Could not remove job from manifest',
            });
        } finally {
            hideGlobalOverlay();
        }
    };

    const fetchThirdPartyJobs = useCallback(async () => {
        if (manifest?.id) {
            setLoadingThirdPartyJobs(true);
            listThirdPartyJobs(manifest.id)
                .then((res) => {
                    setThirdPartJobs([...res].sort((a, b) => b.updatedAt.localeCompare(a.updatedAt)));
                })
                .finally(() => setLoadingThirdPartyJobs(false));
        }
    }, [manifest?.id]);

    const handleDeleteThirdPartyJob = async () => {
        if (!manifest?.id || !selectedThirdPartyJob) return;

        try {
            showGlobalOverlay({ type: 'LoadingOverlay', payload: { message: 'Removing job...' } });
            await deleteThirdPartyJob(manifest.id, selectedThirdPartyJob.id);
            setThirdPartyModal(null);
            setSelectedThirdPartyJob(null);
            await fetchThirdPartyJobs();
            Notification({
                type: 'success',
                placement: 'top',
                messageTitle: 'Job removed',
                description: 'Successfully removed 3rd-party job.',
            });
        } catch (error) {
            Notification({
                type: 'error',
                placement: 'top',
                messageTitle: 'Error',
                description: 'Could not remove 3rd-party job.',
            });
        } finally {
            hideGlobalOverlay();
        }
    };

    // Refresh the jobs when we add existing job
    useEffect(() => {
        const existingJobToManifestSubscriberId = window.EventBus.subscribe(Events.ADDED_EXISTING_JOB_TO_MANIFEST, () =>
            refetch()
        );
        const thirdPartyJobToManifestSubscriberId = window.EventBus.subscribe(
            Events.ADDED_THIRD_PARTY_JOB_TO_MANIFEST,
            () => fetchThirdPartyJobs()
        );

        return () => {
            window.EventBus.unsubscribe(existingJobToManifestSubscriberId);
            window.EventBus.unsubscribe(thirdPartyJobToManifestSubscriberId);
        };
    }, [refetch, fetchThirdPartyJobs]);

    useEffect(() => {
        listIndividuals({ size: 200 });
        listCustomers({ size: 200 });
        fetchThirdPartyJobs();
    }, [listIndividuals, listCustomers, fetchThirdPartyJobs]);

    return (
        <>
            <Space direction="vertical" size={24} style={{ width: '100%' }}>
                <ManifestOverviewHeader canAssignManifests={canAssignManifests} />
                <Row justify="space-between">
                    <Col>
                        <Space size={8}>
                            <SearchBar
                                inputPlaceholder="Search jobs"
                                value={queryInputVal}
                                onSearch={(val) => {
                                    setQueryInputVal(val);
                                    setPage(0);
                                }}
                                onClear={() => {
                                    setQueryInputVal('');
                                    setPage(0);
                                }}
                            />
                            <Button onClick={() => setOpenFiltersModal(true)}>Filter</Button>
                        </Space>
                    </Col>
                    <Col>
                        <Space size={8}>
                            <ArchiveFilter
                                value={archived ? 'archived' : 'active'}
                                onChange={(val) => {
                                    if (val === 'archived') {
                                        setArchived(true);
                                    } else {
                                        setArchived(undefined);
                                    }
                                }}
                            />
                            <DatePicker
                                value={exactDate ? moment(exactDate, 'YYYY-MM-DD') : undefined}
                                onChange={(val) => {
                                    if (!val) {
                                        setExactDate(undefined);
                                        return;
                                    }

                                    setExactDate(val.format('YYYY-MM-DD'));
                                }}
                            />
                            {selectedJobIds.length > 0 && (
                                <Button
                                    onClick={() =>
                                        setModal({
                                            type: 'email-job',
                                            selectedJobIds,
                                        })
                                    }
                                >
                                    Email Job
                                </Button>
                            )}
                        </Space>
                    </Col>
                </Row>

                <Row>
                    <AppliedJobFilters tags={tags} clearFilter={clearFilter} clearAll={clearAll} />
                </Row>

                <JobsTable
                    isUnderManifest
                    loading={isLoading}
                    jobs={data?.list ?? []}
                    onChange={handleTableChange}
                    pagination={pagination}
                    currentSortDirection={sortDirection}
                    currentSortParam={sort}
                    actions={{
                        archive: (job) => {
                            setSelectedJob(job);
                            openArchiveJobModal(job.id);
                        },
                        unarchive: (job) => {
                            setSelectedJob(job);
                            openUnarchiveJobModal(job.id);
                        },
                        assign: (job) => {
                            setSelectedJob(job);
                            openAssignJobModal(job.id);
                        },
                        edit: (job) => {
                            setSelectedJob(job);
                            openEditModal();
                        },
                        removeFromManifest: (job) => {
                            if (job.manifest?.id) {
                                openRemoveJobFromManifestModal(job.id, job.manifest.id);
                            }
                        },
                    }}
                    onRowClick={(record) => {
                        const jobId = record?.id;
                        if (jobId) {
                            navigate(`${jobId}`);
                        }
                    }}
                    rowSelection={rowSelection}
                    optionalColumns={{
                        assignedTo: true,
                        emailNotification: true,
                    }}
                />

                {/* Third-party jobs  */}
                {thirdPartyJobs.length > 0 && (
                    <div style={{ padding: 24, backgroundColor: 'var(--gray-1)', borderRadius: 6, marginTop: 24 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16 }}>
                            <ThirdPartyJobIcon />
                            <H2>3rd-party Jobs</H2>
                        </div>
                        <ThirdPartyJobsTable
                            loading={loadingThirdPartyJobs}
                            dataSource={thirdPartyJobs}
                            pagination={{
                                hideOnSinglePage: true,
                                position: ['bottomCenter'],
                            }}
                            onEdit={(job) => {
                                setSelectedThirdPartyJob(job);
                                setThirdPartyModal('edit');
                            }}
                            onDelete={(job) => {
                                setSelectedThirdPartyJob(job);
                                setThirdPartyModal('delete');
                            }}
                        />
                    </div>
                )}
            </Space>

            {selectedJob && (
                <AssignJobModal
                    open={isAssignOpen}
                    jobData={{
                        jobId: selectedJob.id,
                        teamId: selectedJob?.teamId,
                    }}
                    onSuccess={() => {
                        refetch();
                        closeModal();
                    }}
                    onClose={closeModal}
                />
            )}

            {selectedJob && (
                <EditJobModal open={isEditModalOpen} job={selectedJob} onClose={closeModal} onSuccess={refetch} />
            )}

            <ArchiveJobModal onArchive={handleArchiveJob} open={isArchiveOpen} onClose={closeModal} />

            <UnarchiveJobModal
                onUnarchive={handleUnarchiveJob}
                open={modal.type === 'unarchive'}
                onClose={closeModal}
            />

            <RemoveJobFromManifestModal
                open={isRemoveOpen}
                onClose={closeModal}
                onRemoveJob={handleRemoveJobFroManifest}
            />

            <JobFiltersModal
                open={openFiltersModal}
                filters={filters}
                onClose={() => setOpenFiltersModal(false)}
                onClearAll={clearAll}
                onClearFilter={clearFilter}
                onFiltersChange={handleFiltersChange}
                optionalFilters={{
                    assignedTo: true,
                    customer: true,
                }}
            />

            {manifest && selectedThirdPartyJob && (
                <AddThirdPartyJobModal
                    open={thirdPartyModal === 'edit'}
                    manifestId={manifest.id}
                    thirdPartyJob={selectedThirdPartyJob}
                    onClose={() => {
                        setThirdPartyModal(null);
                        setSelectedThirdPartyJob(null);
                    }}
                />
            )}

            <DeleteThirdPartyJobModal
                open={thirdPartyModal === 'delete'}
                onClose={() => {
                    setThirdPartyModal(null);
                    setSelectedThirdPartyJob(null);
                }}
                onConfirm={handleDeleteThirdPartyJob}
            />

            {manifest?.id && (
                <EmailJobModal
                    key={String(modal.type === 'email-job')}
                    open={modal.type === 'email-job'}
                    manifestId={manifest.id}
                    onClose={closeModal}
                    selectedJobIds={selectedJobIds.map(String)}
                    onSuccess={() => {
                        closeModal();
                        // Give some time to the database to update the jobs respone with the email notifications
                        setTimeout(() => refetch(), 500);
                    }}
                />
            )}
        </>
    );
};

export default ManifestOverview;
